/**
 * A lista do lugar aberto no Desk Cloud (docs/spec/desk-cloud.md, "A página"): o caminho, o título e o
 * resumo, os botões, a busca, os chips por tipo, a ordem e Grade/Lista; com algo selecionado, a barra
 * das ações em massa. Embaixo, as pastas como blocos, os arquivos e o bloco de enviar. O botão direito
 * na área vazia abre o menu dela; o teclado vale aqui dentro (Enter, F2, Delete, Ctrl+A, Esc).
 */
import { useEffect, useState, type KeyboardEvent } from 'react';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { FILE_KINDS, type FileKind, type FileSort } from '../shared.js';
import { useLoggedNav } from '../states/nav.js';
import { useApp } from '../store.js';
import { Button, ComboBox, Spinner } from '../ui/controls.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Icon } from '../ui/icons.js';
import { PageHeader } from '../ui/PageHeader.js';
import { emptyTrash, purgeFiles, renameFile, restoreFiles, trashFiles } from './cloudActions.js';
import { areaMenu, selectionMenu, trashMenu } from './cloudMenus.js';
import { KIND_COLORS, childFolders, folderPath, plural, totalBytes, roomColor, roomInitials, type CloudLocation } from './cloudModel.js';
import { useCloud } from './cloudStore.js';
import { useCloudUi } from './cloudUi.js';
import { FileCard, FileRow, FolderTile } from './CloudItems.js';
import { folderTiles, openRoom } from './CloudSidebar.js';

/** O nome do lugar aberto. */
function useTitle(at: CloudLocation): string {
  const t = useT();
  const ui = useCloudUi();
  const folders = useCloud((s) => s.summary?.folders) ?? [];
  if (at.view === 'room') return ui.roomName(at.roomId);
  if (at.view === 'folder') return folders.find((folder) => folder.id === at.folderId)?.name ?? '';
  return t(`cloud.view.${at.view}`);
}

export function CloudBrowser() {
  const t = useT();
  const ui = useCloudUi();
  const at = useCloud((s) => s.at);
  const kind = useCloud((s) => s.kind);
  const query = useCloud((s) => s.query);
  const sort = useCloud((s) => s.sort);
  const layout = useCloud((s) => s.layout);
  const summary = useCloud((s) => s.summary);
  const page = useCloud((s) => s.page);
  const loading = useCloud((s) => s.loading);
  const loadingMore = useCloud((s) => s.loadingMore);
  const error = useCloud((s) => s.error);
  const selected = useCloud((s) => s.selected);
  const focus = useCloud((s) => s.focus);
  const [search, setSearch] = useState(query);
  const title = useTitle(at);
  const mobile = ui.mobile;
  const trash = at.view === 'trash';
  const folders = summary?.folders ?? [];
  const room = at.view === 'room' ? summary?.rooms.find((r) => r.id === at.roomId) : undefined;
  const files = page?.files ?? [];
  const shownLayout = mobile ? 'list' : layout;
  const tiles = query || kind ? [] : folderTiles(folders, at);
  const canUpload = !trash;
  const selecting = selected.size > 0;

  // a busca sai quando a pessoa para de digitar; trocar de lugar limpa a caixa
  useEffect(() => setSearch(query), [query, at]);
  useEffect(() => {
    if (search === query) return;
    const timer = window.setTimeout(() => useCloud.getState().setQuery(search), 250);
    return () => window.clearTimeout(timer);
  }, [search, query]);

  const selectedFiles = () => useCloud.getState().filesOf(useCloud.getState().selected);
  const where = at.view === 'all' ? t('cloud.view.all').toLocaleLowerCase() : title;

  // o resumo: quantos e o tamanho (o tamanho quando a página tem todos), e o que a mesa ou a lixeira querem dizer
  const subtitle = (() => {
    if (!page) return '';
    const base = page.files.length >= page.total ? t('cloud.subtitle', { files: plural(t, 'cloud.files', page.total), size: formatBytes(totalBytes(page.files)) }) : plural(t, 'cloud.files', page.total);
    if (room) return `${base} · ${room.members ? plural(t, 'cloud.subtitle.room', room.members) : t('cloud.subtitle.roomEmpty')}`;
    if (at.view === 'unlinked') return `${base} · ${t('cloud.subtitle.unlinked')}`;
    if (trash) return `${base} · ${t('cloud.subtitle.trash')}`;
    return base;
  })();

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select')) return;
    const cloud = useCloud.getState();
    const id = target.closest<HTMLElement>('[data-file]')?.dataset.file ?? cloud.focus;
    const file = id ? cloud.filesOf([id])[0] : undefined;
    const acting = cloud.selected.size ? selectedFiles() : file ? [file] : [];
    if (e.key === 'Enter' && file && target.dataset.file) {
      e.preventDefault();
      ui.openFile(file);
    } else if (e.key === 'F2' && file && !trash) {
      e.preventDefault();
      void renameFile(file);
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && acting.length) {
      e.preventDefault();
      void (trash ? purgeFiles(acting) : trashFiles(acting));
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      cloud.setSelection(files.map((f) => f.id));
    } else if (e.key === 'Escape') {
      if (cloud.selected.size) cloud.clearSelection();
      else if (cloud.focus) cloud.setFocus(null);
      else return;
      e.preventDefault();
    }
  };

  const openAreaMenu = (anchor: { x: number; y: number }) =>
    ui.openMenu(
      anchor,
      areaMenu(
        ui.menuContext,
        { trash, canUpload, sort, layout, hasFiles: files.length > 0 },
        {
          upload: () => ui.upload(),
          newFolder: () => ui.folderActions.newFolder(at.view === 'folder' ? at.folderId : null),
          selectAll: () => useCloud.getState().setSelection(files.map((f) => f.id)),
          setSort: (next) => useCloud.getState().setSort(next),
          setLayout: (next) => useCloud.getState().setLayout(next),
          emptyTrash: () => void emptyTrash(),
        },
      ),
      title,
    );

  return (
    <main className={`rd-cloudMain${mobile ? ' is-mobile' : ''}${selecting ? ' is-selecting' : ''}`} aria-label={title}>
      {mobile ? (
        <MobileHead title={title} search={search} setSearch={setSearch} where={where} />
      ) : (
        <PageHeader
          className="rd-cloudHead"
          crumbs={<Crumbs at={at} />}
          title={title}
          subtitle={subtitle}
          actions={
            <>
              {room ? (
                <Button variant="secondary" icon="rooms" onClick={() => openRoom(room.id)}>
                  {t('cloud.openRoom')}
                </Button>
              ) : null}
              {!trash ? (
                <Button variant="secondary" icon="folderPlus" onClick={() => ui.folderActions.newFolder(at.view === 'folder' ? at.folderId : null)}>
                  {t('cloud.newFolder')}
                </Button>
              ) : null}
              {canUpload ? (
                <Button variant="action" icon="upload" onClick={() => ui.upload()}>
                  {t('cloud.upload')}
                </Button>
              ) : null}
            </>
          }
        />
      )}

      {selecting && !mobile ? (
        <BulkBar trash={trash} count={selected.size} all={files.length > 0 && files.every((f) => selected.has(f.id))} />
      ) : (
        <div className="rd-cloudTools">
          {mobile ? null : (
            <label className="rd-searchBox rd-cloudTools__search">
              <Icon name="search" size={16} />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('cloud.search', { where })} aria-label={t('cloud.search', { where })} />
              {search ? (
                <button type="button" className="rd-iconBtn" aria-label={t('cloud.search.clear')} onClick={() => setSearch('')}>
                  <Icon name="close" size={14} />
                </button>
              ) : null}
            </label>
          )}
          <Chips />
          {mobile ? null : (
            <>
              <span className="rd-cloudTools__gap" />
              <ComboBox
                value={sort}
                onChange={(next: FileSort) => useCloud.getState().setSort(next)}
                aria-label={t('cloud.sort')}
                options={(['recent', 'name', 'size'] as const).map((s) => ({ value: s, label: t(`cloud.sort.${s}`) }))}
              />
              <div className="rd-cloudLayout" role="group" aria-label={t('cloud.layout')}>
                {(['grid', 'list'] as const).map((l) => (
                  <button key={l} type="button" className="rd-iconBtn" aria-pressed={layout === l} aria-label={t(`cloud.layout.${l}`)} title={t(`cloud.layout.${l}`)} onClick={() => useCloud.getState().setLayout(l)}>
                    <Icon name={l} size={16} />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div
        className="rd-cloudBody"
        onKeyDown={onKeyDown}
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('.rd-cloudCard, .rd-cloudRow, .rd-cloudFolder, button, input, a')) return;
          e.preventDefault();
          openAreaMenu({ x: e.clientX, y: e.clientY });
        }}
        onClick={(e) => {
          // clicar no vazio solta a seleção e fecha os detalhes
          if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('rd-cloudGrid')) {
            useCloud.getState().clearSelection();
            if (!mobile) useCloud.getState().setFocus(null);
          }
        }}
      >
        {trash ? (
          <p className="rd-cloudNote">
            <Icon name="clock" size={15} />
            {t('cloud.trashNote')}
          </p>
        ) : null}
        {error ? (
          <p className="rd-cloudError" role="alert">
            {error}
          </p>
        ) : null}
        {tiles.length ? (
          <section className="rd-cloudSection" aria-label={t('cloud.folders')}>
            {mobile ? null : <h2 className="rd-cloudSection__title">{t('cloud.folders')}</h2>}
            <div className="rd-cloudFolders">
              {tiles.map((f) => (
                <FolderTile key={f.id} folder={f} />
              ))}
            </div>
          </section>
        ) : null}
        {!page && loading ? (
          <div className="rd-placeholder">
            <Spinner size={28} />
          </div>
        ) : null}
        {page && !files.length ? <Empty where={title} /> : null}
        {files.length ? (
          <section className="rd-cloudSection" aria-label={t('cloud.filesHeading')}>
            {tiles.length && !mobile ? <h2 className="rd-cloudSection__title">{t('cloud.filesHeading')}</h2> : null}
            {shownLayout === 'grid' ? (
              <div className="rd-cloudGrid">
                {files.map((file) => (
                  <FileCard key={file.id} file={file} selected={selected.has(file.id)} focused={focus === file.id} />
                ))}
                {canUpload ? (
                  <button type="button" className="rd-cloudDropTile" onClick={() => ui.upload()}>
                    <Icon name="upload" size={22} />
                    <span>{t('cloud.dropTile')}</span>
                    <span className="rd-cloudDropTile__hint">{t('cloud.dropTile.hint')}</span>
                  </button>
                ) : null}
              </div>
            ) : (
              <div className={`rd-cloudList${mobile ? ' is-compact' : ''}`} role="table" aria-label={t('cloud.filesHeading')}>
                {mobile ? null : (
                  <div className="rd-cloudRow rd-cloudRow--head" role="row">
                    <span role="columnheader" className="rd-cloudRow__check">
                      <input
                        type="checkbox"
                        aria-label={t('cloud.selectAll')}
                        checked={files.every((f) => selected.has(f.id))}
                        onChange={(e) => (e.target.checked ? useCloud.getState().setSelection(files.map((f) => f.id)) : useCloud.getState().clearSelection())}
                      />
                    </span>
                    <span role="columnheader">{t('cloud.col.name')}</span>
                    <span role="columnheader">{t('cloud.col.kind')}</span>
                    <span role="columnheader">{t('cloud.col.access')}</span>
                    <span role="columnheader">{t('cloud.col.size')}</span>
                    <span role="columnheader">{t('cloud.col.date')}</span>
                    <span role="columnheader" />
                  </div>
                )}
                {files.map((file) => (
                  <FileRow key={file.id} file={file} selected={selected.has(file.id)} focused={focus === file.id} compact={mobile} selecting={selecting} />
                ))}
              </div>
            )}
            {page && files.length < page.total ? (
              <Button className="rd-cloudMore__load" busy={loadingMore} onClick={() => void useCloud.getState().loadMore()}>
                {t('cloud.loadMore')}
              </Button>
            ) : null}
          </section>
        ) : null}
      </div>

      {mobile && canUpload && !selecting ? (
        <button type="button" className="rd-cloudFab" aria-label={t('cloud.upload')} onClick={() => ui.upload()}>
          <Icon name="upload" size={24} />
        </button>
      ) : null}
      {mobile && selecting ? <BulkBar trash={trash} count={selected.size} all={files.every((f) => selected.has(f.id))} /> : null}
    </main>
  );
}

/** O caminho do alto: Desk Cloud › Mesas, ou Desk Cloud › Pastas › as mães da pasta aberta. */
function Crumbs({ at }: { at: CloudLocation }) {
  const t = useT();
  const folders = useCloud((s) => s.summary?.folders) ?? [];
  const parts: { label: string; at?: CloudLocation }[] = [{ label: t('cloud.title'), at: { view: 'all' } }];
  if (at.view === 'room' || at.view === 'unlinked') parts.push({ label: t('cloud.section.rooms') });
  if (at.view === 'folder') {
    parts.push({ label: t('cloud.section.folders') });
    for (const folder of folderPath(folders, at.folderId).slice(0, -1)) parts.push({ label: folder.name, at: { view: 'folder', folderId: folder.id } });
  }
  return (
    <nav className="rd-cloudCrumbs" aria-label={t('cloud.where')}>
      {parts.map((part, i) => (
        <span key={i} className="rd-cloudCrumbs__part">
          {i ? <Icon name="chevronRight" size={12} /> : null}
          {part.at ? (
            <button type="button" className="rd-cloudCrumbs__link" onClick={() => useCloud.getState().go(part.at!)}>
              {part.label}
            </button>
          ) : (
            <span>{part.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** Os chips por tipo, com os números da visão (os sem arquivo somem, menos o escolhido). */
function Chips() {
  const t = useT();
  const kind = useCloud((s) => s.kind);
  const kinds = useCloud((s) => s.page?.kinds);
  const all = kinds ? FILE_KINDS.reduce((sum, k) => sum + kinds[k], 0) : 0;
  const chip = (value: FileKind | null, label: string, count: number) => (
    <button key={value ?? 'all'} type="button" className={`rd-chip rd-cloudChip${kind === value ? ' is-on' : ''}`} aria-pressed={kind === value} onClick={() => useCloud.getState().setKind(value)}>
      {value ? <span className="rd-cloudDot" style={{ background: KIND_COLORS[value] }} aria-hidden="true" /> : null}
      {label}
      <span className="rd-cloudChip__n">{count}</span>
    </button>
  );
  return (
    <div className="rd-chips rd-cloudChips" role="group" aria-label={t('cloud.filter')}>
      {chip(null, t('cloud.kinds.all'), all)}
      {FILE_KINDS.filter((k) => (kinds?.[k] ?? 0) > 0 || kind === k).map((k) => chip(k, t(`cloud.kinds.${k}`), kinds?.[k] ?? 0))}
    </div>
  );
}

/** A barra das ações em massa (no celular, presa embaixo). */
function BulkBar({ trash, count, all }: { trash: boolean; count: number; all: boolean }) {
  const t = useT();
  const ui = useCloudUi();
  const files = () => useCloud.getState().filesOf(useCloud.getState().selected);
  const page = useCloud((s) => s.page);
  return (
    <div className={`rd-cloudBulk${ui.mobile ? ' is-mobile' : ''}`} role="toolbar" aria-label={t('cloud.bulk')}>
      <label className="rd-cloudBulk__all">
        <input type="checkbox" checked={all} onChange={() => (all ? useCloud.getState().clearSelection() : useCloud.getState().setSelection(page?.files.map((f) => f.id) ?? []))} />
        {plural(t, 'cloud.selected', count)}
      </label>
      {trash ? (
        <>
          <Button variant="secondary" onClick={() => void restoreFiles(files())}>
            {t('cloud.menu.restore')}
          </Button>
          <Button variant="secondary" className="is-danger" onClick={() => void purgeFiles(files())}>
            {t('cloud.menu.purge')}
          </Button>
        </>
      ) : (
        <>
          <Button variant="secondary" icon="link" onClick={() => ui.actions.share(files())}>
            {ui.mobile ? '' : t('cloud.menu.share')}
          </Button>
          <Button variant="secondary" icon="folder" onClick={() => ui.actions.pickFolder(files())}>
            {ui.mobile ? '' : t('cloud.menu.moveTo')}
          </Button>
          {ui.mobile ? null : (
            <Button variant="secondary" icon="download" onClick={() => ui.actions.download(files())}>
              {t('cloud.menu.zip')}
            </Button>
          )}
          <Button variant="secondary" icon="trash" onClick={() => void trashFiles(files())}>
            {ui.mobile ? '' : t('cloud.menu.trash')}
          </Button>
        </>
      )}
      <span className="rd-cloudTools__gap" />
      <button
        type="button"
        className="rd-iconBtn"
        aria-label={t('cloud.more', { name: plural(t, 'cloud.selected', count) })}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const list = files();
          ui.openMenu({ x: r.left, y: r.bottom }, trash ? trashMenu(ui.menuContext, list, ui.actions) : selectionMenu(ui.menuContext, list, ui.actions), plural(t, 'cloud.selected', count));
        }}
      >
        ⋯
      </button>
      <button type="button" className="rd-iconBtn" aria-label={t('cloud.clearSelection')} onClick={() => useCloud.getState().clearSelection()}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}

/** O vazio de cada lugar: o da busca, o da lixeira, o dos favoritos, o do tipo e o de começo. */
function Empty({ where }: { where: string }) {
  const t = useT();
  const ui = useCloudUi();
  const at = useCloud((s) => s.at);
  const query = useCloud((s) => s.query);
  const kind = useCloud((s) => s.kind);
  const [title, text] = query
    ? [t('cloud.empty.search'), t('cloud.empty.searchText', { q: query, where })]
    : at.view === 'trash'
      ? [t('cloud.empty.trash'), t('cloud.empty.trashText')]
      : at.view === 'favorites'
        ? [t('cloud.empty.favorites'), t('cloud.empty.favoritesText')]
        : kind
          ? [t('cloud.empty.kind'), t('cloud.empty.kindText')]
          : [t('cloud.empty.title'), t('cloud.empty.text')];
  return (
    <EmptyState icon={at.view === 'trash' ? 'trash' : at.view === 'favorites' ? 'star' : 'cloud'} title={title} text={text}>
      {query ? (
        <Button variant="secondary" onClick={() => useCloud.getState().setQuery('')}>
          {t('cloud.search.clear')}
        </Button>
      ) : null}
      {!query && !kind && at.view !== 'trash' && at.view !== 'favorites' ? (
        <Button variant="action" icon="upload" onClick={() => ui.upload()}>
          {t('cloud.empty.pick')}
        </Button>
      ) : null}
    </EmptyState>
  );
}

/**
 * O alto da página no celular (a tela "Celular" do esboço): o nome e a pílula do espaço, a busca e o
 * seletor de lugar (as visões, as mesas e a árvore numa folha), com quantos arquivos há.
 */
function MobileHead({ title, search, setSearch, where }: { title: string; search: string; setSearch: (q: string) => void; where: string }) {
  const t = useT();
  const ui = useCloudUi();
  const at = useCloud((s) => s.at);
  const summary = useCloud((s) => s.summary);
  const page = useCloud((s) => s.page);
  const go = useLoggedNav((s) => s.go);
  const plan = useApp((s) => s.plan);
  // a cota vem do plano de agora: muda na hora com o aviso do servidor
  const usage = summary ? { used: summary.usage.used, quota: plan?.entitlements.storageBytes ?? summary.usage.quota } : null;
  const ratio = usage ? Math.min(1, usage.used / Math.max(1, usage.quota)) : 0;

  const pick = (anchor: { x: number; y: number }) => {
    const folders = summary?.folders ?? [];
    const entry = (label: string, to: CloudLocation) => ({ label, checked: JSON.stringify(to) === JSON.stringify(at), onSelect: () => useCloud.getState().go(to) });
    const tree = (parentId: string | null, depth: number): ReturnType<typeof entry>[] =>
      childFolders(folders, parentId).flatMap((folder) => [entry(`${'  '.repeat(depth)}${folder.name}`, { view: 'folder', folderId: folder.id }), ...tree(folder.id, depth + 1)]);
    ui.openMenu(
      anchor,
      [
        ...(['all', 'recent', 'favorites', 'trash'] as const).map((view) => entry(t(`cloud.view.${view}`), { view })),
        { kind: 'title' as const, label: t('cloud.section.rooms') },
        ...(summary?.rooms ?? []).map((room) => entry(room.name, { view: 'room', roomId: room.id })),
        entry(t('cloud.view.unlinked'), { view: 'unlinked' }),
        ...(folders.length ? [{ kind: 'title' as const, label: t('cloud.section.folders') }, ...tree(null, 0)] : []),
      ],
      t('cloud.where.pick'),
    );
  };

  return (
    <header className="rd-cloudMobileHead">
      <div className="rd-cloudMobileHead__top">
        <span className="rd-cloudNav__logo" aria-hidden="true">
          <Icon name="cloud" size={18} />
        </span>
        <h1 className="rd-cloudMobileHead__title">{t('cloud.title')}</h1>
        {usage ? (
          <button
            type="button"
            className={`rd-cloudPill${usage.used >= usage.quota ? ' is-full' : ''}`}
            aria-label={t('cloud.storage.aria', { used: formatBytes(usage.used), quota: formatBytes(usage.quota) })}
            onClick={() => go({ kind: 'settings', page: 'plan' })}
          >
            <span className="rd-cloudPill__bar" aria-hidden="true">
              <span style={{ width: `${Math.max(ratio * 100, ratio > 0 ? 4 : 0)}%` }} />
            </span>
            {formatBytes(usage.used)} / {formatBytes(usage.quota)}
          </button>
        ) : null}
      </div>
      <label className="rd-searchBox rd-cloudMobileHead__search">
        <Icon name="search" size={16} />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('cloud.search', { where })} aria-label={t('cloud.search', { where })} />
      </label>
      <div className="rd-cloudMobileHead__where">
        <button
          type="button"
          className="rd-cloudWhere"
          aria-label={`${t('cloud.where.pick')}: ${title}`}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            pick({ x: r.left, y: r.bottom });
          }}
        >
          {at.view === 'room' ? (
            <span className="rd-cloudNav__room" style={{ background: roomColor(at.roomId) }} aria-hidden="true">
              {roomInitials(title)}
            </span>
          ) : (
            <Icon name={at.view === 'folder' ? 'folder' : at.view === 'trash' ? 'trash' : at.view === 'favorites' ? 'star' : 'cloud'} size={16} />
          )}
          <span>{title}</span>
          <Icon name="chevronDown" size={16} />
        </button>
        {page ? <span className="rd-cloudMobileHead__count">{plural(t, 'cloud.files', page.total)}</span> : null}
      </div>
    </header>
  );
}
